import { memo } from "react";

import {
  CartLineProvider,
  CartCost,
  type CartStatus,
} from "@shopify/hydrogen-react";
import { type MoneyV2 } from "@shopify/hydrogen-react/storefront-api-types";
import { ErrorBoundary } from "react-error-boundary";

import { CartBanner } from "@cart/CartBanner";
import { CartCheckout } from "@cart/CartCheckout";
import { CartDiscount } from "@cart/CartDiscount";
import { CartEmpty } from "@cart/CartEmpty/CartEmpty.data";
import { CartFreeShippingProgressBar } from "@cart/CartFreeShippingProgressBar";
import { CartGiftNoteStatus } from "@cart/CartGiftNoteStatus/CartGiftNoteStatus.data";
import { CartItem } from "@cart/CartItem";
import { CartItemPlaceholder } from "@cart/CartItemPlaceholder";
import { CartSpendingGitWithPurchaseManager } from "@cart/CartSpendingGiftWithPurchase/CartSpendingGiftWithPurchaseManager.data";
import { CartTieredProgressBar } from "@cart/CartTieredProgressBar";
import { CartUpsells } from "@cart/CartUpsells";
import { Heading } from "@react/components/Heading";
import { Icon } from "@react/components/Icon";
import { LoadingOverlay } from "@react/components/LoadingOverlay";
import { Text } from "@react/components/Text";
import {
  type CartBaseDrawerSettings,
  type CartBaseDrawerContent,
  type CartMerchandiseLine,
  type CartOpenCartDrawer,
  type CartSpendingBasedGiftWithPurchaseSettings,
} from "@tablescape/state";
import { tw } from "classnames";

type Props = {
  btnAddLabel: string;
  btnCloseLabel: string;
  bundleValueText: string;
  cartDiscountTitle: string;
  cartItemPlaceholderCount: number;
  cartLineAdding: boolean;
  cartLineChanging: boolean;
  cartLineRemoving: boolean;
  cartLineUpdating: boolean;
  cartLinkTo: string;
  cartProductGiftItemButtonEditTextLabel: string;
  cartProductGiftItemButtonTextLabel: string;
  cartProductPage: string;
  cartSpendingGiftButtonTextLabel: string;
  cartSpendingGiftTitleLabel: string;
  cartSpendingGiftWithPurchaseSettings: CartSpendingBasedGiftWithPurchaseSettings[];
  content: CartBaseDrawerContent;
  dataGiftNoteStatus: {
    giftButtonText: string;
    giftEditNote: string;
    giftText: string;
  };
  discountedTotalAmount: Partial<MoneyV2> | undefined;
  handleCloseCartDrawer: CartOpenCartDrawer;
  isDrawerVisible: boolean;
  linkAriaLabel: string;
  relatedCartLines: CartMerchandiseLine[] | undefined;
  settings: CartBaseDrawerSettings;
  showDiscount: boolean;
  status: CartStatus;
  subtotalLabel: string;
  totalQuantity: number;
  upsellsEnabled: boolean;
};

const styles = {
  container: tw("flex", "h-full", "flex-col"),
  header: tw(
    "flex",
    "flex-wrap",
    "items-center",
    "border-0",
    "border-b",
    "border-solid",
    "border-divider",
    "bg-background-primary",
    "px-6",
    "pt-2",
    "pb-1"
  ),
  headerTextContainer: tw("flex", "items-baseline"),
  heading: tw("leading-none"),
  closeButton: tw(
    "bg-transparent",
    "border-0",
    "outline-none",
    "ml-auto",
    "-mr-4",
    "w-12",
    "h-10",
    "inline-flex",
    "justify-center",
    "items-center",
    "cursor-pointer"
  ),
  closeIcon: tw("w-6", "h-6"),
  quantity: tw("text-primary-muted", "ml-2"),
  cartLinesContainer: tw("flex", "flex-col", "flex-1", "overflow-y-auto"),
  cartLines: tw("flex-1"),
  cartLine: (cartLineChanging: boolean) =>
    tw("transition-opacity", "opacity-100", {
      "opacity-50": cartLineChanging,
    }),
  footer: tw("mt-auto"),
  summary: tw(
    "border-0",
    "border-solid",
    "border-divider",
    "bg-background-primary",
    "px-6",
    "py-4"
  ),
  discountTotal: tw("mb-1.5", "flex", "justify-between", "text-brick-700"),
  subtotal: tw("mb-4", "flex", "justify-between", "text-primary"),
  legalStatement: tw("mt-2", "text-center"),
  subtotalPlaceholder: tw("opacity-50"),
};

const CartBaseViewBase = ({
  btnAddLabel,
  btnCloseLabel,
  bundleValueText,
  cartDiscountTitle,
  cartItemPlaceholderCount,
  cartLineAdding,
  cartLineChanging,
  cartLineRemoving,
  cartLineUpdating,
  cartLinkTo,
  cartProductGiftItemButtonEditTextLabel,
  cartProductGiftItemButtonTextLabel,
  cartProductPage,
  cartSpendingGiftButtonTextLabel,
  cartSpendingGiftTitleLabel,
  cartSpendingGiftWithPurchaseSettings,
  content,
  dataGiftNoteStatus,
  discountedTotalAmount,
  handleCloseCartDrawer,
  isDrawerVisible,
  linkAriaLabel,
  relatedCartLines,
  settings,
  showDiscount,
  status,
  subtotalLabel,
  totalQuantity,
  upsellsEnabled,
}: Props) => {
  if (status === "fetching" || status === "creating") {
    return <LoadingOverlay show={true} />;
  }

  if (
    (status === "idle" || status === "uninitialized") &&
    totalQuantity === 0
  ) {
    return (
      <CartEmpty baseHeading={content.heading} btnCloseLabel={btnCloseLabel} />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTextContainer}>
          <Heading as="h2" className={styles.heading} size="xs">
            {content.heading}
          </Heading>
          <Text as="body2" className={styles.quantity}>
            {`(${totalQuantity} item${totalQuantity > 1 ? "s" : ""})`}
          </Text>
        </div>
        <button className={styles.closeButton} onClick={handleCloseCartDrawer}>
          <Icon className={styles.closeIcon} name="close" />
        </button>
        {settings.freeShippingProgressBar.enabled && (
          <CartFreeShippingProgressBar
            progressBarVisible={isDrawerVisible}
            {...content.freeShippingProgressBar}
            {...settings.freeShippingProgressBar}
          />
        )}
      </div>
      {content.banner && <CartBanner content={content.banner} />}
      {settings.tieredProgressBar.enabled && (
        <CartTieredProgressBar
          progressBarVisible={isDrawerVisible}
          {...content.tieredProgressBar}
          {...settings.tieredProgressBar}
        />
      )}
      <div className={styles.cartLinesContainer}>
        <CartSpendingGitWithPurchaseManager
          buttonTextLabel={cartSpendingGiftButtonTextLabel}
          giftWithPurchaseSettings={cartSpendingGiftWithPurchaseSettings}
          titleLabel={cartSpendingGiftTitleLabel}
        />
        <ol className={styles.cartLines}>
          {cartLineAdding &&
            Array.from({ length: cartItemPlaceholderCount }, (_, index) => (
              <CartItemPlaceholder key={`cartItemPlaceholder-${index}`} />
            ))}
          {relatedCartLines?.map((line) => {
            return (
              !!line && (
                <CartLineProvider key={line.id} line={line}>
                  <CartItem
                    bundleValueText={bundleValueText}
                    cartLinkTo={cartLinkTo}
                    cartProductGiftItemButtonEditTextLabel={
                      cartProductGiftItemButtonEditTextLabel
                    }
                    cartProductGiftItemButtonTextLabel={
                      cartProductGiftItemButtonTextLabel
                    }
                    cartProductPage={cartProductPage}
                    linkAriaLabel={linkAriaLabel}
                    selectedGiftsWithPurchase={line?.selectedGiftsWithPurchase}
                  />
                </CartLineProvider>
              )
            );
          })}
        </ol>
        {upsellsEnabled && (
          <ErrorBoundary fallback={null}>
            <CartUpsells
              badgeText={content.upsellBadgeText}
              btnAddLabel={btnAddLabel}
              bundleValueText={bundleValueText}
              headingText={content.upsellHeadingText}
            />
          </ErrorBoundary>
        )}
        <LoadingOverlay show={cartLineUpdating || cartLineRemoving} />
      </div>
      <div className={styles.footer}>
        <CartGiftNoteStatus
          giftButtonText={dataGiftNoteStatus.giftButtonText}
          giftEditNote={dataGiftNoteStatus.giftEditNote}
          giftText={dataGiftNoteStatus.giftText}
        />

        <div className={styles.summary}>
          <CartDiscount
            cartLineChanging={cartLineChanging}
            discountedTotalAmount={discountedTotalAmount}
            showDiscount={showDiscount}
            title={cartDiscountTitle}
          />
          <Text as="body2" className={styles.subtotal}>
            <span>{subtotalLabel}</span>
            {cartLineChanging ? (
              <span className={styles.subtotalPlaceholder}>---</span>
            ) : (
              <CartCost amountType="subtotal" as="span" withoutTrailingZeros />
            )}
          </Text>
          <CartCheckout />
          <Text as="caption" className={styles.legalStatement}>
            <span
              className="antialiased [&>a]:text-misc-legal-blue"
              dangerouslySetInnerHTML={{
                __html: content.legalStatement,
              }}
            ></span>
          </Text>
        </div>
      </div>
    </div>
  );
};

export const CartBaseView = memo(CartBaseViewBase);
